import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminLogin, adminLogout, adminMe, fetchLeads, updateLeadStatus, formatApiError } from "@/lib/api";

const STATUSES = ["NEW", "CONTACTED", "QUALIFIED", "PROPOSAL", "WON", "LOST"];

const STATUS_COLORS = {
  NEW: "bg-amber/15 text-amber",
  CONTACTED: "bg-blue-500/15 text-blue-400",
  QUALIFIED: "bg-emerald-500/15 text-emerald-400",
  PROPOSAL: "bg-purple-500/15 text-purple-400",
  WON: "bg-green-600/20 text-green-400",
  LOST: "bg-red-500/15 text-red-400",
};

const inputCls = "w-full bg-surface border border-linestrong text-white text-sm px-3 py-2.5 focus:outline-none focus:border-amber transition-colors";

export function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminLogin(email, password);
      navigate("/admin/leads");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-surface border border-linestrong p-8" data-testid="admin-login-page">
        <p className="font-display font-extrabold uppercase text-white text-xl">Extreme Teambuilding</p>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-dim mt-1">Administrace</p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block text-xs font-mono uppercase tracking-wider text-mist mb-1.5">E-mail</label>
            <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" className={inputCls} data-testid="admin-email-input" />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs font-mono uppercase tracking-wider text-mist mb-1.5">Heslo</label>
            <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" className={inputCls} data-testid="admin-password-input" />
          </div>
          {error && <p className="text-sm text-red-400" role="alert" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} className="w-full bg-amber hover:bg-amberdark disabled:opacity-50 text-white font-display font-bold uppercase tracking-wide py-3 transition-colors" data-testid="admin-login-submit">
            {loading ? "Přihlašujeme…" : "Přihlásit"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function AdminLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = async (status) => {
    try {
      const { data } = await fetchLeads(status || undefined);
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else setError(formatApiError(err));
    }
  };

  useEffect(() => {
    adminMe().then(() => load()).catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = async (lead, status) => {
    try {
      await updateLeadStatus(lead.id, status);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      if (selected?.id === lead.id) setSelected({ ...selected, status });
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const logout = async () => {
    await adminLogout().catch(() => {});
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-leads-page">
      <header className="border-b border-line bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <p className="font-display font-extrabold uppercase text-white">Leady <span className="text-dim font-mono text-xs ml-2">Extreme Teambuilding</span></p>
          <div className="flex items-center gap-4">
            <a href="/admin/seo-audit" className="text-xs font-mono uppercase tracking-wider text-mist hover:text-white" data-testid="admin-seo-audit-link">SEO audit</a>
            <button onClick={logout} className="text-xs font-mono uppercase tracking-wider text-mist hover:text-white" data-testid="admin-logout-button">Odhlásit</button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-status-filters">
          <button onClick={() => { setFilter(""); load(""); }} className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 border ${filter === "" ? "border-amber text-amber" : "border-linestrong text-mist"}`} data-testid="filter-all">Vše</button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => { setFilter(s); load(s); }} className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 border ${filter === s ? "border-amber text-amber" : "border-linestrong text-mist"}`} data-testid={`filter-${s.toLowerCase()}`}>
              {s}
            </button>
          ))}
        </div>
        {error && <p className="text-sm text-red-400 mb-4" role="alert" data-testid="admin-error">{error}</p>}
        {!leads ? (
          <p className="text-mist text-sm">Načítáme…</p>
        ) : leads.length === 0 ? (
          <div className="border border-dashed border-linestrong p-10 text-center" data-testid="admin-no-leads">
            <p className="text-mist text-sm">Zatím žádné leady. Nové poptávky z webu se objeví tady.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-line">
            <table className="w-full text-left text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="bg-elevated text-xs font-mono uppercase tracking-wider text-mist">
                  <th className="px-4 py-3">Datum</th>
                  <th className="px-4 py-3">Firma</th>
                  <th className="px-4 py-3">Kontakt</th>
                  <th className="px-4 py-3">Lidí</th>
                  <th className="px-4 py-3">Balíček</th>
                  <th className="px-4 py-3">Zdroj</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {leads.map((l) => (
                  <tr key={l.id} className="bg-surface hover:bg-elevated transition-colors" data-testid={`lead-row-${l.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 text-dim whitespace-nowrap">{new Date(l.createdAt).toLocaleDateString("cs-CZ")}</td>
                    <td className="px-4 py-3 text-white font-semibold">{l.company}</td>
                    <td className="px-4 py-3 text-mist">{l.contactName}<br /><span className="text-xs text-dim">{l.email}</span></td>
                    <td className="px-4 py-3 text-mist">{l.participants || "—"}</td>
                    <td className="px-4 py-3 text-mist">{l.packageInterest || "—"}</td>
                    <td className="px-4 py-3 text-dim text-xs">{l.landingPage || l.source || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-mono font-bold px-2 py-1 ${STATUS_COLORS[l.status] || ""}`} data-testid={`lead-status-${l.id.slice(0, 8)}`}>{l.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(l)} className="text-xs font-mono uppercase tracking-wider text-amber hover:text-white" data-testid={`lead-detail-${l.id.slice(0, 8)}`}>Detail</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/80 backdrop-blur-sm p-4" role="dialog" aria-modal="true" data-testid="lead-detail-modal">
          <div className="bg-surface border border-linestrong w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-display font-extrabold uppercase text-white text-xl">{selected.company}</p>
                <p className="text-xs font-mono text-dim mt-1">{new Date(selected.createdAt).toLocaleString("cs-CZ")} · {selected.landingPage || "web"}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-mist hover:text-white text-sm font-mono uppercase" data-testid="lead-detail-close">Zavřít</button>
            </div>
            <dl className="mt-5 space-y-2.5 text-sm">
              {[
                ["Kontakt", `${selected.contactName} · ${selected.email}${selected.phone ? ` · ${selected.phone}` : ""}`],
                ["Počet účastníků", selected.participants],
                ["Preferovaný termín", selected.preferredDate],
                ["Odkud", selected.originCity],
                ["Balíček", selected.packageInterest],
                ["Budget", selected.budget],
                ["UTM", [selected.utm_source, selected.utm_medium, selected.utm_campaign, selected.utm_term].filter(Boolean).join(" / ")],
                ["Poznámka", selected.message],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3">
                  <dt className="text-dim text-xs font-mono uppercase tracking-wider pt-0.5">{k}</dt>
                  <dd className="col-span-2 text-mist whitespace-pre-wrap">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6">
              <p className="text-xs font-mono uppercase tracking-wider text-dim mb-2">Změnit status</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => changeStatus(selected, s)}
                    className={`text-[11px] font-mono font-bold px-3 py-1.5 border ${selected.status === s ? "border-amber text-amber" : "border-linestrong text-mist hover:text-white"}`}
                    data-testid={`lead-status-btn-${s.toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
